import { useEffect, useRef } from 'react';

export type Listener = (message: any) => void;
export type Publisher = (message: any) => void;

const channelSubscriptions: { [key: number]: Set<Listener> } = {};
let channelCount = 0;

export const makePubSubChannel = () => channelCount++;

/**
 * Returns the same channel on every call.
 * @return {number}
 */
export const usePubSubChannel = (): number => {
  const channelRef = useRef<number>(undefined);
  if (channelRef.current == null) {
    channelRef.current = makePubSubChannel();
  }
  // eslint-disable-next-line react-hooks/refs
  return channelRef.current;
};

/**
 * @param {number} channel
 * @return {Function} A function that publishes a message to all subscribers on the given channel
 */
export const usePublish = (channel: number): Publisher => {
  return (message: any) => {
    const subscriptions = channelSubscriptions[channel];
    if (subscriptions) {
      subscriptions.forEach((listener) => {
        listener(message);
      });
    }
  };
};

/**
 * @param {number} channel
 * @param {Function} listener A function that's called with messages published on the given channel
 */
export const useSubscribe = (channel: number, listener: Listener): void => {
  if (!channelSubscriptions[channel]) {
    // eslint-disable-next-line react-hooks/immutability
    channelSubscriptions[channel] = new Set();
  }
  channelSubscriptions[channel].add(listener);

  // Unsubscribe on unmount, or if the same component passes in different params
  useEffect(() => {
    return () => {
      channelSubscriptions[channel].delete(listener);
    };
  }, [channel, listener]);
};
